/**
 * Privacy Policy Page
 * Complete privacy policy for Rely platform
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@chakra-ui/react';
import { MdSecurity } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import LegalPageLayout from '../../components/legal/LegalPageLayout';
import {
  LegalSection,
  LegalParagraph,
  LegalList,
  LegalDisclaimer,
  LegalTable,
  LegalSubSection,
  LegalContact,
  LegalDivider,
} from '../../components/legal/LegalSection';
import { LEGAL_CONFIG } from '../../config/legal';

const PrivacyPage = () => {
  const { t } = useTranslation();

  return (
    <LegalPageLayout
      title={t('privacy.title', 'Política de Privacidad')}
      subtitle={t(
        'privacy.subtitle',
        'Cómo recopilamos, usamos y protegemos su información'
      )}
      documentType="privacy"
      icon={MdSecurity}
    >
      {/* Introduction */}
      <LegalParagraph>
        {t(
          'privacy.intro',
          `${LEGAL_CONFIG.company.name} ("nosotros", "nos" o "nuestro") opera la plataforma rely.business. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos su información cuando utiliza nuestros servicios.`
        )}
      </LegalParagraph>

      {/* Section 1: Information We Collect */}
      <LegalSection
        number={1}
        title={t('privacy.collect.title', 'Información que Recopilamos')}
        id="information-collected"
      >
        <LegalSubSection
          title={t('privacy.collect.personal.title', 'Información Personal')}
        >
          <LegalParagraph>
            {t(
              'privacy.collect.personal.description',
              'Recopilamos información que usted nos proporciona directamente, incluyendo:'
            )}
          </LegalParagraph>
          <LegalList
            items={[
              t('privacy.collect.personal.name', 'Nombre completo'),
              t(
                'privacy.collect.personal.email',
                'Dirección de correo electrónico'
              ),
              t('privacy.collect.personal.phone', 'Número de teléfono'),
              t('privacy.collect.personal.address', 'Dirección física'),
              t(
                'privacy.collect.personal.ssn',
                'Número de Seguro Social o ITIN (para trámites de EIN)'
              ),
              t(
                'privacy.collect.personal.passport',
                'Información de pasaporte (si aplica)'
              ),
              t('privacy.collect.personal.payment', 'Información de pago'),
            ]}
          />
        </LegalSubSection>

        <LegalSubSection
          title={t('privacy.collect.business.title', 'Información Empresarial')}
        >
          <LegalList
            items={[
              t('privacy.collect.business.name', 'Nombre de la empresa'),
              t('privacy.collect.business.type', 'Tipo de negocio'),
              t(
                'privacy.collect.business.members',
                'Información de miembros/directores'
              ),
              t(
                'privacy.collect.business.agent',
                'Dirección del agente registrado'
              ),
            ]}
          />
        </LegalSubSection>

        <LegalSubSection
          title={t('privacy.collect.technical.title', 'Información Técnica')}
        >
          <LegalList
            items={[
              t('privacy.collect.technical.ip', 'Dirección IP'),
              t('privacy.collect.technical.browser', 'Tipo de navegador'),
              t(
                'privacy.collect.technical.device',
                'Información del dispositivo'
              ),
              t(
                'privacy.collect.technical.cookies',
                'Cookies y tecnologías similares'
              ),
              t('privacy.collect.technical.logs', 'Registros de acceso y uso'),
            ]}
          />
        </LegalSubSection>
      </LegalSection>

      {/* Section 2: How We Use Information */}
      <LegalSection
        number={2}
        title={t('privacy.use.title', 'Cómo Usamos su Información')}
        id="information-use"
      >
        <LegalParagraph>
          {t('privacy.use.intro', 'Utilizamos la información recopilada para:')}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'privacy.use.services',
              'Proporcionar y mantener nuestros servicios'
            ),
            t(
              'privacy.use.formation',
              'Procesar formaciones de empresas y documentos'
            ),
            t('privacy.use.ein', 'Solicitar EIN ante el IRS'),
            t(
              'privacy.use.communicate',
              'Comunicarnos con usted sobre su cuenta'
            ),
            t('privacy.use.support', 'Proporcionar soporte al cliente'),
            t('privacy.use.improve', 'Mejorar nuestros servicios'),
            t('privacy.use.legal', 'Cumplir con obligaciones legales'),
            t('privacy.use.fraud', 'Prevenir fraude y actividades ilegales'),
          ]}
        />
      </LegalSection>

      {/* Section 3: Legal Basis */}
      <LegalSection
        number={3}
        title={t('privacy.basis.title', 'Base Legal para el Procesamiento')}
        id="legal-basis"
      >
        <LegalParagraph>
          {t('privacy.basis.intro', 'Procesamos su información basándonos en:')}
        </LegalParagraph>
        <LegalTable
          headers={[
            t('privacy.basis.base', 'Base Legal'),
            t('privacy.basis.description', 'Descripción'),
          ]}
          rows={[
            [
              t('privacy.basis.contract', 'Ejecución del Contrato'),
              t(
                'privacy.basis.contract.desc',
                'Necesario para proporcionar los servicios que solicitó'
              ),
            ],
            [
              t('privacy.basis.legal', 'Obligación Legal'),
              t(
                'privacy.basis.legal.desc',
                'Cumplimiento de leyes fiscales y regulaciones'
              ),
            ],
            [
              t('privacy.basis.consent', 'Consentimiento'),
              t(
                'privacy.basis.consent.desc',
                'Para comunicaciones de marketing (si aplica)'
              ),
            ],
            [
              t('privacy.basis.interest', 'Interés Legítimo'),
              t(
                'privacy.basis.interest.desc',
                'Mejorar servicios y prevenir fraude'
              ),
            ],
          ]}
        />
      </LegalSection>

      {/* Section 4: Data Retention */}
      <LegalSection
        number={4}
        title={t('privacy.retention.title', 'Retención de Datos')}
        id="data-retention"
      >
        <LegalParagraph>
          {t(
            'privacy.retention.intro',
            'Retenemos su información de acuerdo con los siguientes períodos:'
          )}
        </LegalParagraph>
        <LegalTable
          headers={[
            t('privacy.retention.type', 'Tipo de Dato'),
            t('privacy.retention.period', 'Período de Retención'),
          ]}
          rows={Object.entries(LEGAL_CONFIG.dataRetention).map(
            ([key, value]) => [
              t(
                `privacy.retention.${key}`,
                key.replace(/([A-Z])/g, ' $1').trim()
              ),
              value,
            ]
          )}
        />
      </LegalSection>

      {/* Section 5: Information Sharing */}
      <LegalSection
        number={5}
        title={t('privacy.sharing.title', 'Compartir Información')}
        id="information-sharing"
      >
        <LegalParagraph>
          {t('privacy.sharing.intro', 'Podemos compartir su información con:')}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'privacy.sharing.government',
              'Agencias gubernamentales (IRS, Secretaria de Estado) para procesar formaciones'
            ),
            t(
              'privacy.sharing.providers',
              'Proveedores de servicios (procesadores de pago, almacenamiento en la nube)'
            ),
            t(
              'privacy.sharing.partners',
              'Socios bancarios (Mercury, Relay) cuando solicite cuentas'
            ),
            t(
              'privacy.sharing.legal',
              'Autoridades legales cuando lo requiera la ley'
            ),
          ]}
        />
        <LegalDisclaimer
          type="info"
          title={t('privacy.sharing.noSale.title', 'No Vendemos sus Datos')}
        >
          {t(
            'privacy.sharing.noSale.text',
            'No vendemos, alquilamos ni compartimos su informacion personal con terceros para fines de marketing.'
          )}
        </LegalDisclaimer>
      </LegalSection>

      {/* Section 6: Data Security */}
      <LegalSection
        number={6}
        title={t('privacy.security.title', 'Seguridad de Datos')}
        id="data-security"
      >
        <LegalParagraph>
          {t(
            'privacy.security.intro',
            'Implementamos medidas de seguridad para proteger su informacion:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'privacy.security.encryption',
              'Encriptacion de datos en transito y en reposo'
            ),
            t('privacy.security.access', 'Controles de acceso estrictos'),
            t('privacy.security.audit', 'Auditorias de seguridad regulares'),
            t('privacy.security.monitoring', 'Monitoreo continuo de amenazas'),
            t(
              'privacy.security.soc2',
              'Cumplimiento con estandares de seguridad de la industria'
            ),
          ]}
        />
      </LegalSection>

      {/* Section 7: Your Rights */}
      <LegalSection
        number={7}
        title={t('privacy.rights.title', 'Sus Derechos')}
        id="your-rights"
      >
        <LegalParagraph>
          {t(
            'privacy.rights.intro',
            'Usted tiene los siguientes derechos respecto a su informacion:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'privacy.rights.access',
              'Derecho a acceder a su informacion personal'
            ),
            t(
              'privacy.rights.correction',
              'Derecho a corregir informacion inexacta'
            ),
            t(
              'privacy.rights.deletion',
              'Derecho a solicitar eliminacion de sus datos'
            ),
            t('privacy.rights.portability', 'Derecho a portabilidad de datos'),
            t(
              'privacy.rights.restriction',
              'Derecho a restringir el procesamiento'
            ),
            t(
              'privacy.rights.objection',
              'Derecho a objetar ciertos procesamientos'
            ),
          ]}
        />
        <LegalParagraph>
          {t(
            'privacy.rights.exercise',
            `Para ejercer estos derechos, contacte a ${LEGAL_CONFIG.contact.privacy}`
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 8: Cookies */}
      <LegalSection
        number={8}
        title={t('privacy.cookies.title', 'Cookies y Tecnologías Similares')}
        id="cookies"
      >
        <LegalParagraph>
          {t(
            'privacy.cookies.intro',
            'Utilizamos cookies y tecnologías similares para mejorar su experiencia. Para información detallada sobre las cookies que utilizamos, consulte nuestra '
          )}
          <Link as={RouterLink} to="/cookies" color="brand.500">
            {t('privacy.cookies.link', 'Política de Cookies')}
          </Link>
          .
        </LegalParagraph>
      </LegalSection>

      {/* Section 9: Children's Privacy */}
      <LegalSection
        number={9}
        title={t('privacy.children.title', 'Privacidad de Menores')}
        id="children"
      >
        <LegalParagraph>
          {t(
            'privacy.children.text',
            'Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos intencionalmente información de menores. Si descubrimos que hemos recopilado información de un menor, la eliminaremos de inmediato.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 10: International Transfers */}
      <LegalSection
        number={10}
        title={t(
          'privacy.international.title',
          'Transferencias Internacionales'
        )}
        id="international"
      >
        <LegalParagraph>
          {t(
            'privacy.international.text',
            'Su información puede ser transferida y procesada en Estados Unidos, donde se encuentran nuestros servidores. Al utilizar nuestros servicios, usted consiente a esta transferencia.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 11: Changes */}
      <LegalSection
        number={11}
        title={t('privacy.changes.title', 'Cambios a esta Política')}
        id="changes"
      >
        <LegalParagraph>
          {t(
            'privacy.changes.text',
            'Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos de cambios materiales publicando la nueva política en esta página y actualizando la fecha de "Última actualización".'
          )}
        </LegalParagraph>
      </LegalSection>

      <LegalDivider />

      {/* Contact Information */}
      <LegalSection
        title={t('privacy.contact.title', 'Contacto de Privacidad')}
        id="contact"
      >
        <LegalParagraph>
          {t(
            'privacy.contact.text',
            'Si tiene preguntas sobre esta Política de Privacidad o desea ejercer sus derechos:'
          )}
        </LegalParagraph>
        <LegalContact
          label={t('privacy.contact.officer', 'Oficial de Privacidad')}
          email={LEGAL_CONFIG.contact.privacy}
          description={t(
            'privacy.contact.officer.description',
            'Para consultas sobre privacidad y datos personales'
          )}
        />
      </LegalSection>
    </LegalPageLayout>
  );
};

export default PrivacyPage;
